import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format } from "date-fns";
import { ArrowRight, ArrowLeft, CalendarIcon, Check, Loader2, Rocket, MapPin, Navigation } from "lucide-react";

const STEPS = ["Route", "When", "Seats & price", "Car & details", "Preferences", "Review"];

const PublishRide = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    from_city: "", to_city: "", from_coords: null, to_coords: null,
    date: undefined, time: "09:00", seats_total: "3", price_per_seat: "300",
    car: "", description: "",
    preferences: { chatty: "depends", music: true, smoking: false, pets: false },
    instant_booking: true,
  });
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setPref = (patch) => setForm((f) => ({ ...f, preferences: { ...f.preferences, ...patch } }));

  const canNext = () => {
    if (step === 0) return form.from_city && form.to_city;
    if (step === 1) return form.date && form.time;
    if (step === 2) return Number(form.seats_total) >= 1 && Number(form.price_per_seat) >= 0;
    return true;
  };

  const next = () => { if (!canNext()) { toast.error("Please complete this step"); return; } setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        from_city: form.from_city, to_city: form.to_city,
        from_coords: form.from_coords, to_coords: form.to_coords,
        date: format(form.date, "yyyy-MM-dd"), time: form.time,
        seats_total: parseInt(form.seats_total), price_per_seat: parseFloat(form.price_per_seat),
        car: form.car || null, description: form.description,
        preferences: form.preferences, instant_booking: form.instant_booking,
      };
      const res = await api.post("/rides", payload);
      toast.success("Ride published — ready for liftoff! 🚀");
      navigate(`/rides/${res.data.ride_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not publish ride");
    } finally { setSubmitting(false); }
  };

  const times = Array.from({ length: 24 }).flatMap((_, h) => ["00", "30"].map((m) => `${String(h).padStart(2, "0")}:${m}`));

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center gap-2 mb-2"><Rocket className="text-[#F7C948]" size={20} /><h1 className="font-display text-2xl font-semibold">Publish a ride</h1></div>
      <p className="text-muted-cosmic text-sm mb-5">Step {step + 1} of {STEPS.length} — {STEPS[step]}</p>
      <Progress value={((step + 1) / STEPS.length) * 100} className="h-2 bg-white/10 mb-6" />

      <Card className="glass p-6">
        {step === 0 && (
          <div className="space-y-4">
            <div><Label className="text-secondary-cosmic mb-1 block">Leaving from</Label>
              <CityAutocomplete value={form.from_city} placeholder="City of departure" icon={<Navigation size={16} />} onSelect={(name, coords) => set({ from_city: name, from_coords: coords })} onChangeText={(t) => set({ from_city: t })} /></div>
            <div><Label className="text-secondary-cosmic mb-1 block">Going to</Label>
              <CityAutocomplete value={form.to_city} placeholder="Destination city" icon={<MapPin size={16} />} onSelect={(name, coords) => set({ to_city: name, to_coords: coords })} onChangeText={(t) => set({ to_city: t })} /></div>
          </div>
        )}
        {step === 1 && (
          <div className="space-y-4">
            <div><Label className="text-secondary-cosmic mb-1 block">Date</Label>
              <Popover><PopoverTrigger asChild>
                <Button type="button" variant="outline" className="w-full h-11 justify-start bg-white/5 border-white/10 text-white font-normal"><CalendarIcon size={16} className="mr-2 text-[#22D3EE]" />{form.date ? format(form.date, "EEE, dd MMM yyyy") : <span className="text-white/40">Pick a date</span>}</Button>
              </PopoverTrigger><PopoverContent className="w-auto p-0 glass-solid border-white/10"><Calendar mode="single" selected={form.date} onSelect={(d) => set({ date: d })} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} initialFocus /></PopoverContent></Popover>
            </div>
            <div><Label className="text-secondary-cosmic mb-1 block">Departure time</Label>
              <Select value={form.time} onValueChange={(v) => set({ time: v })}><SelectTrigger className="h-11 bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger><SelectContent className="glass-solid text-white max-h-64">{times.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-4">
            <div><Label className="text-secondary-cosmic mb-1 block">Available seats</Label>
              <Select value={form.seats_total} onValueChange={(v) => set({ seats_total: v })}><SelectTrigger className="h-11 bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger><SelectContent className="glass-solid text-white">{[1,2,3,4,5,6].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label className="text-secondary-cosmic mb-1 block">Price per seat (฿)</Label>
              <Input type="number" min="0" value={form.price_per_seat} onChange={(e) => set({ price_per_seat: e.target.value })} className="h-11 bg-white/5 border-white/10 text-white" /></div>
            <div className="flex items-center justify-between glass-solid rounded-lg p-3"><div><p className="text-white">Instant booking</p><p className="text-xs text-muted-cosmic">Let passengers book without approval</p></div><Switch checked={form.instant_booking} onCheckedChange={(v) => set({ instant_booking: v })} /></div>
          </div>
        )}
        {step === 3 && (
          <div className="space-y-4">
            <div><Label className="text-secondary-cosmic mb-1 block">Car (optional)</Label><Input value={form.car} onChange={(e) => set({ car: e.target.value })} placeholder="e.g. Tesla Model 3" className="h-11 bg-white/5 border-white/10 text-white" /></div>
            <div><Label className="text-secondary-cosmic mb-1 block">Description (optional)</Label><Textarea value={form.description} onChange={(e) => set({ description: e.target.value })} placeholder="Anything passengers should know — stops, luggage, meeting point…" className="bg-white/5 border-white/10 text-white min-h-24" /></div>
          </div>
        )}
        {step === 4 && (
          <div className="space-y-3">
            {[{ k: "music", label: "Music on board" }, { k: "pets", label: "Pets welcome" }, { k: "smoking", label: "Smoking allowed" }].map((p) => (
              <div key={p.k} className="flex items-center justify-between glass-solid rounded-lg p-3"><span className="text-white">{p.label}</span><Switch checked={form.preferences[p.k]} onCheckedChange={(v) => setPref({ [p.k]: v })} /></div>
            ))}
            <div><Label className="text-secondary-cosmic mb-1 block">Chattiness</Label>
              <Select value={form.preferences.chatty} onValueChange={(v) => setPref({ chatty: v })}><SelectTrigger className="h-11 bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger><SelectContent className="glass-solid text-white"><SelectItem value="love">I love to chat</SelectItem><SelectItem value="depends">Depends on my mood</SelectItem><SelectItem value="quiet">Quiet ride please</SelectItem></SelectContent></Select>
            </div>
          </div>
        )}
        {step === 5 && (
          <div className="space-y-3">
            <h3 className="font-display text-lg">Review your ride</h3>
            <div className="glass-solid rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-cosmic">Route</span><span className="text-white">{form.from_city} → {form.to_city}</span></div>
              <div className="flex justify-between"><span className="text-muted-cosmic">When</span><span className="text-white">{form.date ? format(form.date, "dd MMM yyyy") : "—"} at {form.time}</span></div>
              <div className="flex justify-between"><span className="text-muted-cosmic">Seats</span><span className="text-white">{form.seats_total}</span></div>
              <div className="flex justify-between"><span className="text-muted-cosmic">Price</span><span className="text-[#FFF7D6]">{form.price_per_seat} ฿ / seat</span></div>
              {form.car && <div className="flex justify-between"><span className="text-muted-cosmic">Car</span><span className="text-white">{form.car}</span></div>}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-6">
          <Button type="button" variant="ghost" onClick={back} disabled={step === 0} data-testid="publish-ride-back-button" className="text-white hover:bg-white/6 gap-1"><ArrowLeft size={16} /> Back</Button>
          {step < STEPS.length - 1 ? (
            <Button type="button" onClick={next} data-testid="publish-ride-next-button" className="bg-[hsl(var(--primary))] text-white glow-violet hover:glow-cyan gap-1">Next <ArrowRight size={16} /></Button>
          ) : (
            <Button type="button" onClick={submit} disabled={submitting} data-testid="publish-ride-submit-button" className="bg-[hsl(var(--primary))] text-white glow-violet hover:glow-cyan gap-2">{submitting ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />} Publish ride</Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default PublishRide;
